import React from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { Card } from '@/components/ui';
import { adminUpdateUserProfile } from '@/lib/auth/client-auth-service';
import type { User } from '@/lib/types/core';

type RowUser = Pick<User, 'id' | 'name' | 'default_buyin_method' | 'roles' | 'auto_add_to_game'>;

function highestRole(roles: RowUser['roles']): string {
  if (roles?.app_admin) return 'Admin';
  if (roles?.entertainment_admin) return 'Ent. Admin';
  if (roles?.player) return 'Player';
  return '—';
}

type BuyInMethod = RowUser['default_buyin_method'];

const BUY_IN_METHODS: BuyInMethod[] = ['digital', 'cash'];

function formatBuyIn(method: BuyInMethod | undefined | null): string {
  if (!method) return '—';
  return method === 'digital' ? 'Digital' : 'Cash';
}

export function AdminUsersTable({ users }: { users: RowUser[] }) {
  const navigate = useNavigate();
  const [savingId, setSavingId] = React.useState<string | null>(null);

  const updateField = async <K extends keyof RowUser>(
    event: React.MouseEvent,
    user: RowUser,
    field: K,
    value: RowUser[K]
  ) => {
    event.stopPropagation();
    setSavingId(user.id);
    try {
      await adminUpdateUserProfile(user.id, {
        fields: { [field]: value },
      });
    } catch (error) {
      toast.error(error instanceof Error ? error.message : `Failed to update ${String(field)}.`);
    } finally {
      setSavingId(null);
    }
  };

  const toggleAutoAdd = (event: React.ChangeEvent<HTMLInputElement>, user: RowUser) => {
    event.stopPropagation();
    void updateField(event as unknown as React.MouseEvent, user, 'auto_add_to_game', !user.auto_add_to_game);
  };

  const cycleBuyIn = (event: React.MouseEvent, user: RowUser) => {
    const currentIndex = BUY_IN_METHODS.indexOf(user.default_buyin_method ?? 'digital');
    const nextMethod = BUY_IN_METHODS[(currentIndex + 1) % BUY_IN_METHODS.length];
    void updateField(event, user, 'default_buyin_method', nextMethod);
  };

  return (
    <Card className="border border-gray-200 overflow-hidden p-0">
      <div className="overflow-x-auto">
        <table className="w-full text-left text-sm">
          <thead className="bg-aura-background text-[10px] font-black uppercase tracking-wider text-aura-text/60">
            <tr>
              <th className="px-4 py-3">Name</th>
              <th className="px-4 py-3">Buy-in</th>
              <th className="px-4 py-3">Role</th>
              <th className="px-4 py-3 text-center">Auto-add</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {users.map((user, index) => (
              <tr
                key={user.id}
                onClick={() => navigate(`/admin/users/${user.id}`)}
                className={`cursor-pointer transition-colors ${
                  index % 2 === 1 ? 'bg-aura-primary/[0.03]' : 'bg-white'
                } hover:bg-aura-primary/5`}
              >
                <td className="px-4 py-3">
                  <div className="font-semibold text-aura-text">{user.name}</div>
                </td>
                <td className="px-4 py-3" onClick={(e) => e.stopPropagation()}>
                  <button
                    type="button"
                    aria-label={`Change buy-in method for ${user.name}`}
                    disabled={savingId === user.id}
                    onClick={(e) => cycleBuyIn(e, user)}
                    className="px-3 py-1 rounded-full text-xs font-medium border border-aura-primary/30 bg-aura-primary/10 text-aura-primary hover:bg-aura-primary/20 disabled:opacity-50 transition-colors"
                  >
                    {savingId === user.id ? '...' : formatBuyIn(user.default_buyin_method)}
                  </button>
                </td>
                <td className="px-4 py-3 text-aura-text/70">{highestRole(user.roles)}</td>
                <td className="px-4 py-3 text-center" onClick={(e) => e.stopPropagation()}>
                  <label className="inline-flex items-center gap-2 cursor-pointer">
                    <input
                      type="checkbox"
                      aria-label={`Auto-add ${user.name} to new games`}
                      checked={user.auto_add_to_game === true}
                      disabled={savingId === user.id}
                      onChange={(e) => toggleAutoAdd(e, user)}
                      className="disabled:opacity-50"
                    />
                    {savingId === user.id && <span className="text-xs text-aura-text/50">...</span>}
                  </label>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Card>
  );
}
